import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { GridColDef } from "@mui/x-data-grid"
import { isEqual, isNull } from "lodash"
import { IconButton } from "@mui/material"

import Divider from "@/components/divider/Divider"
import Toggle from "@/components/form/toggle/Toggle"
import Table from "@/components/table/Table"
import { overflowPagination } from "@/components/table/Pagination"
import BasicButton from "@/components/button/BasicButton"
import Icon from "@/components/icon/Icon"
import { colors } from "@/components/colors"
import TextField from "@/components/form/textField/TextField"
import RangeInput from "@/app/settings/metric/zones/components/RangeInput"
import PlayerMetricCell from "@/app/settings/metric/zones/components/PlayerMetricCell"
import ActiveToggleCell from "@/app/settings/metric/zones/components/ActiveToggleCell"
import PlayerMaxCell from "@/app/settings/metric/zones/components/PlayerMaxCell"
import {
  ZoneTabContentProps,
  ZoneTabContentRangesType,
  ZoneTabContentValuesType,
} from "@/app/settings/metric/zones/components/type"
import CustomMetricHistoryDialog from "@/app/settings/metric/zones/components/CustomMetricHistoryDialog"
import ExportDialog from "@/app/settings/metric/zones/components/ExportDialog"
import ImportDialog from "@/app/settings/metric/zones/components/ImportDialog"
import {
  DEFAULT_TYPE_UNIT,
  NEW_TYPE,
  ZONE_CODE_BY_TYPE,
} from "@/app/settings/metric/zones/components/constants"
import { useCalculateUnitQuery, useUnitQuery } from "@/hooks/api/useSettingQuery"
import { createGlobalStore } from "@/stores/global"
import { Item } from "@/types/rangeInput"
import { PlayerMetric } from "@/types/metric"
import { sortPlayerMetrics } from "@/app/settings/metric/zones/utils/sortPlayerMetrics"
import {
  generateZones,
  getCustomHrRanges,
  getCustomRanges,
  getHrRanges,
  getRanges,
  getValues,
} from "@/utils/range"
import {
  HR_BLOCKED_KEYS,
  isAllowedZoneCellInput,
  checkIsErrCustomThreshold,
} from "@/app/settings/metric/zones/components/validation"
import { convertUnitSquare } from "@/utils/helper"
import { createCustomHistoryDialogStore } from "@/app/settings/store/zone"
import { useBlockBrowserBack } from "@/hooks/router/useBlockBrowserBack"
import { useNativeBridge } from "@/hooks/useNativeBridge"
import { createZoneSettingState } from "@/stores/settings"

import "./zoneTabContent.scss"

const EMPTY_ITEMS: Item[] = []
const EMPTY_VALUES: Array<string | number> = []

export default function ZoneTabContent({
  type,
  zoneSettingType,
  zones,
  isLoading,
  mutateAsync,
  minMax = { min: 0, max: 10 },
  minMaxCount = { min: 5, max: 10 },
  minMaxPlayerMetric = { min: 0, max: 100 },
  minMaxPlayerMetricCount = { min: 5, max: 10 },
  editedData,
  setEditedData,
  hideSaveButton = false,
  onSave,
}: ZoneTabContentProps) {
  const { t, i18n } = useTranslation()
  const { isWebView } = useNativeBridge()
  const setSnackBar = createGlobalStore((state) => state.setSnackBar)
  const setIsHistoryDialogOpen = createCustomHistoryDialogStore((state) => state.setIsOpen)
  const setHistoryDialogPlayer = createCustomHistoryDialogStore((state) => state.setIdfPlayer)
  const { setHasChanges, setIsAllValid } = createZoneSettingState()

  const tableRef = useRef<HTMLDivElement>(null)
  const [isExportOpen, setIsExportOpen] = useState(false)
  const [isImportOpen, setIsImportOpen] = useState(false)

  const { data: units } = useUnitQuery()
  const { data: calcMax } = useCalculateUnitQuery({
    type: NEW_TYPE[type],
    value: minMax.max,
  })
  const { data: calcMin } = useCalculateUnitQuery({
    type: NEW_TYPE[type],
    value: minMax.min,
  })

  const fixedNumber = type === "hr" ? 0 : 1

  const unit = useMemo(() => {
    const zoneUnit = units?.find((unit) => unit.type === NEW_TYPE[type])

    return {
      [type]: {
        unit: convertUnitSquare(zoneUnit?.unit || DEFAULT_TYPE_UNIT[type]),
        decimalPoint: zoneUnit?.value ?? 1,
      },
    }
  }, [units, type])

  const min = calcMin?.result ?? minMax.min
  const max = calcMax?.result ?? minMax.max

  const initialGlobalRanges = useMemo<ZoneTabContentRangesType | undefined>(() => {
    if (!zones?.global) {
      return undefined
    }

    return {
      [type]:
        type === "hr"
          ? getHrRanges({
              max: zones.global.length + 1,
              suffix: "%",
              func: getCustomHrRanges,
            }).slice(1)
          : getRanges({
              max: zones.global.length + 1,
              suffix: unit?.[type].unit ?? "",
              func: getCustomRanges,
            }),
    }
  }, [zones?.global, type, unit])

  const initialIndividualRanges = useMemo<ZoneTabContentRangesType | undefined>(() => {
    if (!zones?.individual) {
      return undefined
    }

    return {
      [type]: getRanges({
        max: zones.individual.length + 1,
        suffix: "%",
        func: getCustomRanges,
      }),
    }
  }, [zones?.individual, type])

  const initialGlobalValues = useMemo<ZoneTabContentValuesType | undefined>(() => {
    if (!zones?.global) {
      return undefined
    }

    return {
      [type]: getValues(zones.global, unit?.[type].decimalPoint),
    }
  }, [zones?.global, type, unit])

  const initialIndividualValues = useMemo<ZoneTabContentValuesType | undefined>(() => {
    if (!zones?.individual) {
      return undefined
    }

    return {
      [type]: getValues(zones.individual, unit?.[type].decimalPoint).map(
        (value) => `${Number(value).toFixed(fixedNumber)}`,
      ),
    }
  }, [zones?.individual, type, unit, fixedNumber])

  // 토글 On → Custom On → 등번호 오름차순 → 이름. 서버 응답 순서에 의존하지 않는다 (#170)
  const customSorting = useCallback((playerMetrics: PlayerMetric[]) => {
    return sortPlayerMetrics(playerMetrics)
  }, [])

  const initialPlayerMetrics = useMemo<PlayerMetric[]>(() => {
    if (!zones?.playerMetrics) {
      return []
    }

    return customSorting(
      zones.playerMetrics.map((zone) => {
        const basePlayer = {
          ...zone,
          isExpanded: zone.customMetricActive ?? false,
        }

        if (zone.customMetrics) {
          return {
            ...basePlayer,
            max: isNull(zone.max) ? null : Number(zone.max).toFixed(fixedNumber),
            customMetrics: zone.customMetrics.map((metric) => ({
              ...metric,
              value: Number(metric.value).toFixed(fixedNumber),
            })),
          } as PlayerMetric
        }

        return basePlayer
      }),
    )
  }, [zones?.playerMetrics, fixedNumber, customSorting])

  // 과거에 소수로 저장된 값도 화면·저장 모두 정수로 정규화 (버그 #7)
  const initialMaxHr = Math.round(zones?.maxHr ?? 210)

  const [globalRanges, setGlobalRanges] = useState<ZoneTabContentRangesType | undefined>()
  const [individualRanges, setIndividualRanges] = useState<ZoneTabContentRangesType | undefined>()
  const [globalValues, setGlobalValues] = useState<ZoneTabContentValuesType | undefined>()
  const [individualValues, setIndividualValues] = useState<ZoneTabContentValuesType | undefined>()
  const [playerMetrics, setPlayerMetrics] = useState<PlayerMetric[]>([])
  const [maxHr, setMaxHr] = useState<number | string>(210)
  const [globalZoneErr, setGlobalZoneErr] = useState(false)
  const [individualZoneErr, setIndividualZoneErr] = useState(false)
  // 활성 토글을 On 으로 켠 행 — 그 행의 Max 입력란으로 커서를 옮긴다 (ERR-020)
  const [focusMaxPlayer, setFocusMaxPlayer] = useState<number | null>(null)
  const isInitializedRef = useRef(false)

  const individualValuesRef = useRef(type === "hr" ? globalValues : individualValues)
  const findRowIndex = useCallback(
    (idfPlayer: number) => playerMetrics.findIndex((player) => player.idfPlayer === idfPlayer),
    [playerMetrics],
  )

  const inputErr = useMemo(() => {
    const isNegative = min < 0 && max <= 0
    const step = type === "hr" ? 1 : 0.1

    return playerMetrics.some((player) => {
      if (player.active && !player.max) {
        return true
      }

      if (player.customMetricActive && player.customMetrics) {
        const rangeLength = (type === "hr" ? globalRanges : individualRanges)?.[type]?.length ?? 0

        return player.customMetrics.some((metric, i) => {
          if (i === rangeLength - 1) {
            return false
          }

          const values = player.customMetrics?.map((m) => m.value) ?? []

          return checkIsErrCustomThreshold(
            metric.value,
            i,
            values,
            player.customMetricActive,
            isNegative,
            step,
            min,
            max,
          )
        })
      }

      return false
    })
  }, [playerMetrics, min, max, type, globalRanges, individualRanges])

  useEffect(() => {
    if (isInitializedRef.current) {
      return
    }

    if (editedData && units && calcMin && calcMax) {
      const globalLength = editedData.global.length + 1
      const individualLength = editedData.individual.length + 1

      const restoredGlobalRanges = {
        [type]:
          type === "hr"
            ? getHrRanges({
                max: globalLength,
                suffix: "%",
                func: getCustomHrRanges,
              }).slice(1)
            : getRanges({
                max: globalLength,
                suffix: unit?.[type].unit ?? "",
                func: getCustomRanges,
              }),
      }

      const restoredIndividualRanges = {
        [type]: getRanges({
          max: individualLength,
          suffix: "%",
          func: getCustomRanges,
        }),
      }

      setGlobalRanges(restoredGlobalRanges)
      setIndividualRanges(restoredIndividualRanges)
      setGlobalValues({ [type]: editedData.global.map((item) => item.value) })
      setIndividualValues({ [type]: editedData.individual.map((item) => String(item.value)) })
      setPlayerMetrics(editedData.playerMetrics)
      setMaxHr(editedData.maxHr ?? 210)
      isInitializedRef.current = true

      return
    }

    if (
      zones &&
      units &&
      calcMin &&
      calcMax &&
      initialGlobalRanges &&
      initialIndividualRanges &&
      initialGlobalValues &&
      initialIndividualValues
    ) {
      setGlobalRanges(initialGlobalRanges)
      setIndividualRanges(initialIndividualRanges)
      setGlobalValues(initialGlobalValues)
      setIndividualValues(initialIndividualValues)
      setPlayerMetrics(initialPlayerMetrics)
      setMaxHr(initialMaxHr)
      isInitializedRef.current = true
    }
  }, [
    editedData,
    zones,
    units,
    calcMin,
    calcMax,
    unit,
    type,
    initialGlobalRanges,
    initialIndividualRanges,
    initialGlobalValues,
    initialIndividualValues,
    initialPlayerMetrics,
    initialMaxHr,
  ])

  useEffect(() => {
    individualValuesRef.current = type === "hr" ? globalValues : individualValues
  }, [individualValues, globalValues, type])

  useEffect(() => {
    const currentRanges = type === "hr" ? globalRanges : individualRanges
    const rangeLength = currentRanges?.[type]?.length ?? 0

    if (rangeLength === 0 || playerMetrics.length === 0) {
      return
    }

    setPlayerMetrics((prev) =>
      prev.map((player) => {
        if (!player.customMetrics) {
          return player
        }

        const currentLength = player.customMetrics.length
        if (currentLength === rangeLength) {
          return player
        }

        if (currentLength < rangeLength) {
          const newMetrics = [...player.customMetrics]
          for (let i = currentLength; i < rangeLength; i++) {
            const zoneCode = `${ZONE_CODE_BY_TYPE[type]}${type === "hr" ? i : i + 1}`
            newMetrics.push({
              code: zoneCode,
              value: "",
              type: "ZONE",
            })
          }

          return { ...player, customMetrics: newMetrics }
        }

        return {
          ...player,
          customMetrics: player.customMetrics.slice(0, rangeLength),
        }
      }),
    )
  }, [globalRanges, individualRanges, type])

  useEffect(() => {
    if (type === "hr") {
      return
    }

    if (!globalRanges?.[type]) {
      return
    }

    const globalLength = globalRanges[type].length
    const targetValuesLength = globalLength - 1

    if (!individualRanges?.[type]) {
      const newRanges = getRanges({
        max: globalLength,
        suffix: "%",
        func: getCustomRanges,
      })
      setIndividualRanges({ [type]: newRanges })

      setIndividualValues((prev) => {
        const newValues = Array(targetValuesLength).fill("0")

        return { ...prev, [type]: newValues }
      })

      return
    }

    const individualLength = individualRanges[type].length

    if (globalLength > individualLength) {
      const newRanges = getRanges({
        max: globalLength,
        suffix: "%",
        func: getCustomRanges,
      })
      setIndividualRanges({ [type]: newRanges })

      setIndividualValues((prev) => {
        if (!prev?.[type]) {
          const newValues = Array(targetValuesLength).fill("0")

          return { ...prev, [type]: newValues }
        }

        const currentValues = [...prev[type]]
        for (let i = currentValues.length; i < targetValuesLength; i++) {
          currentValues.push("0")
        }

        return { ...prev, [type]: currentValues }
      })
    }
  }, [globalRanges, individualRanges, type])

  const onChangePlayerMetricActive = async (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (mutateAsync) {
      try {
        await mutateAsync({ active: checked })

        setSnackBar({
          open: true,
          message: t("messages.successUpdate"),
          severity: "success",
        })
      } catch (error) {
        return error
      }
    }
  }

  const clearMaxFocus = useCallback(() => setFocusMaxPlayer(null), [])

  const toggleRowExpanded = useCallback((idfPlayer: number) => {
    setPlayerMetrics((prev) =>
      prev.map((player) =>
        player.idfPlayer === idfPlayer ? { ...player, isExpanded: !player.isExpanded } : player,
      ),
    )
  }, [])

  const columns: (GridColDef & { unit?: boolean })[] = useMemo(() => {
    if (individualRanges?.[type] && globalRanges?.[type]) {
      const basicCols: (GridColDef & { unit?: boolean })[] = [
        {
          field: "active",
          headerName: "",
          sortable: false,
          renderCell: (params) => (
            <ActiveToggleCell
              params={params}
              playerMetrics={playerMetrics}
              setPlayerMetrics={setPlayerMetrics}
              individualRanges={type === "hr" ? globalRanges : individualRanges}
              type={type}
              rowIndex={findRowIndex(params.row.idfPlayer)}
              onActivate={setFocusMaxPlayer}
            />
          ),
          width: 56,
        },
        {
          field: "uniformNumber",
          renderHeader: () => t("playerName"),
          sortable: false,
          renderCell: (params) => {
            const isExpanded = params.row.isExpanded ?? false

            return (
              <div className="zone-tab-table-cell">
                <span>
                  <div className="player-name-with-toggle">
                    <p className="ellipsis">{`${params.row.uniformNumber} ${params.row.name}`}</p>
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpanded(params.row.idfPlayer)}
                      sx={{ padding: 0, marginLeft: "8px" }}>
                      <Icon
                        type={isExpanded ? "arrow-up" : "arrow-down"}
                        color={colors.neutral.grey[500]}
                        size={20}
                      />
                    </IconButton>
                  </div>
                </span>
                {isExpanded && (
                  <span>
                    <BasicButton
                      className="zone-tab-table-custom-threshold-title"
                      type="ghost"
                      size="sm"
                      onClick={() => {
                        setIsHistoryDialogOpen(true)
                        setHistoryDialogPlayer(params.row.idfPlayer)
                      }}>
                      <p>{t("customMetric")}</p>
                      <Icon type="information-fill" size={20} color={colors.neutral.grey[500]} />
                    </BasicButton>
                  </span>
                )}
              </div>
            )
          },
          width: 196,
        },
        {
          field: "max",
          sortable: false,
          unit: true,
          renderHeader: () => (
            <div>
              {t(`metric.max${type.charAt(0).toUpperCase() + type.slice(1)}`)}
              <br />
              <span className="zone-tab-table-header-unit">{unit?.[type].unit}</span>
            </div>
          ),
          renderCell: (params) => (
            <PlayerMaxCell
              params={params}
              setPlayerMetrics={setPlayerMetrics}
              rowIndex={findRowIndex(params.row.idfPlayer)}
              min={min}
              max={max}
              type={type}
              shouldFocus={focusMaxPlayer === params.row.idfPlayer}
              onFocused={clearMaxFocus}
            />
          ),
          width: 104,
        },
      ]

      const rangeCols: (GridColDef & { unit?: boolean })[] = (
        (type === "hr" ? globalRanges : individualRanges)?.[type] ?? []
      ).map((range, index) => {
        const zoneField = range.label.split(" ")[0].toLowerCase() + range.label.split(" ")[1]

        return {
          field: zoneField,
          unit: true,
          sortable: false,
          renderHeader: () => (
            <div>
              {t(zoneField).split("(")[0]}
              <br />
              <span className="zone-tab-table-header-unit">{unit?.[type].unit}</span>
            </div>
          ),
          renderCell: (params) => {
            return (
              <PlayerMetricCell
                individualRanges={type === "hr" ? globalRanges : individualRanges}
                type={type}
                playerMetrics={playerMetrics}
                individualValuesRef={individualValuesRef}
                setPlayerMetrics={setPlayerMetrics}
                params={params}
                index={index}
                min={min}
                max={max}
                findRowIndex={findRowIndex}
              />
            )
          },
          width: 140,
        }
      })

      return [...basicCols, ...rangeCols]
    }

    return []
  }, [
    globalRanges,
    individualRanges,
    playerMetrics,
    unit,
    t,
    i18n.language,
    focusMaxPlayer,
    clearMaxFocus,
  ])

  const rows = useMemo(() => playerMetrics, [playerMetrics])

  const isEqualValues = useMemo(() => {
    const currentGlobal = {
      [type]: globalValues?.[type]?.map(
        (value) => `${Number(value).toFixed(unit?.[type].decimalPoint)}`,
      ),
    }
    const zonesGlobal = {
      [type]: getValues(zones?.global || [], unit?.[type].decimalPoint),
    }
    const isSameGlobalValues = isEqual(currentGlobal, zonesGlobal)

    const currentIndividual = {
      [type]: individualValues?.[type]?.map((value) => `${Number(value).toFixed(fixedNumber)}`),
    }
    const zonesIndividual = {
      [type]: getValues(zones?.individual || [], unit?.[type].decimalPoint).map(
        (value) => `${Number(value).toFixed(fixedNumber)}`,
      ),
    }
    const isSameIndividualValues = isEqual(currentIndividual, zonesIndividual)

    const currentPlayerMetrics = customSorting(playerMetrics).map((player) => {
      const newPlayer = { ...player }
      delete newPlayer.customMetricActive
      delete newPlayer.isExpanded
      newPlayer.max = Number(newPlayer.max).toFixed(fixedNumber)

      if (newPlayer.customMetrics) {
        newPlayer.customMetrics = newPlayer.customMetrics.filter((metric) => metric.value !== "")
      }

      return newPlayer
    })
    const zonesPlayerMetrics = customSorting(zones?.playerMetrics ?? []).map((player) => {
      const newPlayer = { ...player }
      delete newPlayer.customMetricActive
      delete newPlayer.isExpanded
      newPlayer.max = Number(newPlayer.max).toFixed(fixedNumber)

      if (newPlayer.customMetrics) {
        return {
          ...newPlayer,
          customMetrics: newPlayer.customMetrics.map((metric) => {
            return {
              ...metric,
              value: Number(metric.value).toFixed(fixedNumber),
            }
          }),
        }
      }

      return newPlayer
    }) as PlayerMetric[]
    const isSameCustomMetics = isEqual(currentPlayerMetrics, zonesPlayerMetrics)

    const isSameMaxHr = type === "hr" ? maxHr === zones?.maxHr : true

    return isSameCustomMetics && isSameIndividualValues && isSameGlobalValues && isSameMaxHr
  }, [
    globalValues,
    individualValues,
    playerMetrics,
    zones,
    maxHr,
    type,
    unit,
    fixedNumber,
    customSorting,
  ])

  const isDisabledSaveBtn = useMemo(() => {
    const maxAccErr = playerMetrics.some((player) => {
      if (player.max && player.active) {
        return Number(player.max) < min || Number(player.max) > max
      }
    })

    return isEqualValues || maxAccErr || isLoading || inputErr || globalZoneErr || individualZoneErr
  }, [
    isEqualValues,
    playerMetrics,
    isLoading,
    inputErr,
    min,
    max,
    globalZoneErr,
    individualZoneErr,
  ])

  useBlockBrowserBack(!isEqualValues)

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isEqualValues) {
        e.preventDefault()
        e.returnValue = ""
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [isEqualValues])

  useEffect(() => {
    if (!isInitializedRef.current || !globalValues || !individualValues) {
      return
    }

    setHasChanges(zoneSettingType, !isEqualValues)
    setIsAllValid(zoneSettingType, !isDisabledSaveBtn || isEqualValues)

    if (setEditedData) {
      const global = generateZones(globalValues[type], `${ZONE_CODE_BY_TYPE[type]}`)
      const individual = generateZones(individualValues[type], `${ZONE_CODE_BY_TYPE[type]}`)
      setEditedData({
        global,
        individual,
        playerMetrics,
        maxHr: type === "hr" ? maxHr : undefined,
      })
    }
  }, [globalValues, individualValues, playerMetrics, maxHr, isEqualValues, isDisabledSaveBtn])

  const zoneCount = useMemo(() => {
    return ((type === "hr" ? globalRanges : individualRanges)?.[type] ?? []).length
  }, [type, globalRanges, individualRanges])

  const onSaveZone = async (customPlayerMetrics?: PlayerMetric[]) => {
    if (!globalValues || !individualValues) {
      return
    }
    const global = generateZones(globalValues[type], `${ZONE_CODE_BY_TYPE[type]}`)
    const individual = generateZones(individualValues[type], `${ZONE_CODE_BY_TYPE[type]}`)
    const metricsToSave = customPlayerMetrics ?? playerMetrics
    const metrics = metricsToSave.map((item) => {
      const { idfPlayer, active, max, customMetricActive, customMetrics } = item
      const codeExceptZone = ZONE_CODE_BY_TYPE[type].slice(0, -4)
      const maxCode = `max${codeExceptZone.charAt(0).toUpperCase() + codeExceptZone.slice(1)}`

      return {
        idfPlayer,
        max: {
          code: maxCode,
          value: max,
        },
        active,
        customMetricActive,
        customMetrics:
          type === "hr"
            ? customMetrics?.map((metric, index) => {
                return {
                  code: `${metric.code.split("Zone")[0]}Zone${index}`,
                  value: metric.value,
                  type: metric.type,
                }
              })
            : customMetrics?.filter((metric) => metric.value !== ""),
      }
    })

    const payload =
      type === "hr"
        ? {
            global,
            individual: global,
            playerMetrics: metrics,
            maxHr: {
              code: "maxHr",
              value: maxHr,
            },
          }
        : { global, individual, playerMetrics: metrics }
    await onSave?.(payload)
  }

  const isErrMaxHrInput = !maxHr || Number(maxHr) > max || Number(maxHr) < min
  const maxHrMessage = useMemo(() => {
    if (!maxHr && maxHr !== 0) {
      return t("rules.required")
    }
    if (Number(maxHr) > max || Number(maxHr) < min) {
      return t("messages.hrInputError", { min, max })
    }

    return ""
  }, [maxHr])

  return (
    <div className="ZoneTabCont">
      <h1 className="title-label">{t("team")}</h1>
      {type === "hr" && (
        <div className="TeamMaxHrCont">
          <p>{t("maxHr")}</p>
          <TextField
            type="number"
            value={maxHr ?? ""}
            // HR 은 정수만 — 소수점·지수·부호 키를 막고, 붙여넣기 등으로 들어온 소수도 반영하지 않는다 (버그 #7)
            onKeyDown={(e) => HR_BLOCKED_KEYS.includes(e.key) && e.preventDefault()}
            onChange={(e) =>
              isAllowedZoneCellInput("hr", e.target.value) && setMaxHr(e.target.value)
            }
            onBlur={(e) => e.target.value && setMaxHr(Math.round(Number(e.target.value)))}
            error={isErrMaxHrInput}
            showMessage={isErrMaxHrInput}
            message={maxHrMessage}
          />
        </div>
      )}
      <div className="TeamZoneCont">
        <RangeInput
          items={globalRanges?.[type] ?? EMPTY_ITEMS}
          setItems={(item: Item[]) => setGlobalRanges({ [type]: item })}
          value={globalValues?.[type] ?? EMPTY_VALUES}
          setValue={(value) => setGlobalValues({ [type]: [...value] })}
          min={type === "hr" ? minMaxPlayerMetric.min : min}
          max={type === "hr" ? minMaxPlayerMetric.max : max}
          minCount={minMaxCount.min}
          maxCount={minMaxCount.max}
          step={type === "hr" ? 1 : 0.1}
          onError={setGlobalZoneErr}
        />
      </div>

      <Divider />

      <h1 className="title-label">
        {t("individual")}
        <Toggle
          checked={zones?.playerMetricActive ?? false}
          onChange={onChangePlayerMetricActive}
        />
      </h1>
      {zones?.playerMetricActive ? (
        <>
          {type !== "hr" && (
            <RangeInput
              items={individualRanges?.[type] ?? EMPTY_ITEMS}
              setItems={(item: Item[]) => setIndividualRanges({ [type]: item })}
              value={individualValues?.[type] ?? EMPTY_VALUES}
              setValue={(value) => setIndividualValues((prev) => ({ ...prev, [type]: value }))}
              min={minMaxPlayerMetric?.min}
              max={minMaxPlayerMetric?.max}
              minCount={globalRanges?.[type]?.length ?? minMaxCount.min}
              maxCount={minMaxPlayerMetricCount.max}
              onError={setIndividualZoneErr}
            />
          )}

          {columns.length ? (
            <>
              {!isWebView && (
                <div className="ZoneActionBar">
                  <BasicButton
                    type="default-outlined"
                    size="sm"
                    icon="download"
                    onClick={() => setIsExportOpen(true)}>
                    {t("button.export")}
                  </BasicButton>
                  <BasicButton
                    type="default-outlined"
                    size="sm"
                    icon="upload"
                    onClick={() => setIsImportOpen(true)}>
                    {t("button.import")}
                  </BasicButton>
                </div>
              )}
              <div className="ZonePlayerCont">
                <Table
                  columns={columns}
                  rows={rows}
                  getRowId={(row: PlayerMetric) => row.idfPlayer}
                  ref={tableRef}
                  getRowHeight={(params) => (params.model.isExpanded ? 112 : 56)}
                  slots={{
                    pagination: overflowPagination(rows.length),
                  }}
                  loading={isLoading}
                  slotProps={{
                    loadingOverlay: {
                      variant: "linear-progress",
                      noRowsVariant: "linear-progress",
                    },
                  }}
                  pinnedColumns={{
                    left: ["active", "uniformNumber"],
                  }}
                  disableColumnResize
                  disableVirtualization
                  disableColumnMenu
                  disableRowSelectionOnClick
                  sx={{
                    // 입력 폼 성격의 테이블이라 hover 하이라이트를 끔 (WEB-2232)
                    // 행 배경은 공용 규칙(table.scss), 고정 컬럼 셀은 자체 pinning(stickyPinning.ts)이
                    // 각자 hover 회색을 칠하므로 둘 다 덮어야 균일함. sticky 셀은 가로 스크롤 시
                    // 비침 방지로 불투명 배경이 필요해 투명 대신 기본 배경(surface-1)으로 고정
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "transparent !important",
                    },
                    '& .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field="active"], & .MuiDataGrid-row:hover .MuiDataGrid-cell[data-field="uniformNumber"]':
                      {
                        backgroundColor: "var(--token-bg-surface-1) !important",
                      },
                  }}
                />
              </div>
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <></>
      )}

      {!hideSaveButton && (
        <div className="ZoneTabButtonCont">
          <BasicButton
            type="primary-contained"
            size="lg"
            onClick={() => onSaveZone()}
            disabled={isDisabledSaveBtn}>
            {t("button.save")}
          </BasicButton>
        </div>
      )}

      <CustomMetricHistoryDialog
        onClose={() => {
          setIsHistoryDialogOpen(false)
          setHistoryDialogPlayer(null)
        }}
        type={type}
        unit={unit?.[type].unit ?? ""}
      />

      <ExportDialog
        open={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        type={type}
        playerMetrics={playerMetrics}
        zoneCount={zoneCount}
      />

      <ImportDialog
        open={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        type={type}
        playerMetrics={playerMetrics}
        setPlayerMetrics={setPlayerMetrics}
        onSave={(updatedMetrics) => onSaveZone(updatedMetrics)}
      />
    </div>
  )
}
